import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

// A handful of ready-made combinations of corner-radius + shadow — the
// starting point every site has before an admin saves their own.
export const BUILT_IN_IMAGE_PRESETS = [
  { preset_id: "builtin-clean", name: "Clean", radius: 0, shadow: "none", builtin: true },
  { preset_id: "builtin-soft-card", name: "Soft Card", radius: 12, shadow: "soft", builtin: true },
  { preset_id: "builtin-rounded-tile", name: "Rounded Tile", radius: 24, shadow: "medium", builtin: true },
  { preset_id: "builtin-polaroid", name: "Polaroid", radius: 0, shadow: "strong", builtin: true },
  { preset_id: "builtin-fully-rounded", name: "Fully Rounded", radius: 80, shadow: "soft", builtin: true },
];

export function useImageStylePresets() {
  const [customPresets, setCustomPresets] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/image-style-presets`, { withCredentials: true });
      setCustomPresets(res.data?.presets || []);
    } catch {
      // Not signed in as admin, or endpoint unreachable — built-ins only.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const savePreset = async (name, radius, shadow) => {
    try {
      await axios.post(
        `${API}/admin/image-style-presets`,
        { name, radius, shadow },
        { withCredentials: true }
      );
      toast.success(`Saved "${name}" preset`);
      await refresh();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save preset");
      return false;
    }
  };

  const deletePreset = async (presetId) => {
    try {
      await axios.delete(`${API}/admin/image-style-presets/${presetId}`, { withCredentials: true });
      toast.success("Preset deleted");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete preset");
    }
  };

  return {
    presets: [...BUILT_IN_IMAGE_PRESETS, ...customPresets],
    loading,
    savePreset,
    deletePreset,
  };
}
